import React, { useState } from 'react';
import ModalShell from '../ModalShell/ModalShell';
import type { GridSize } from '../../types';
import './ScenarioSettingsModal.css';
import { useI18n } from '../../i18n/I18nContext';
import NumberInput from '../NumberInput/NumberInput';

interface ScenarioSettingsModalProps {
  title?: string;
  saveLabel?: string;
  scenarioName: string;
  gridSize: GridSize;
  isDefault: boolean;
  showPrices: boolean;
  itemizePrintItems: boolean;
  canSetDefaultScenario: boolean;
  onClose: () => void;
  onSave: (input: {
    currentName: string;
    nextName: string;
    gridCols: number;
    gridRows: number;
    isDefault: boolean;
    showPrices: boolean;
    itemizePrintItems: boolean;
  }) => void;
}

const ScenarioSettingsModal: React.FC<ScenarioSettingsModalProps> = ({
  title,
  saveLabel,
  scenarioName,
  gridSize,
  isDefault: initialIsDefault,
  showPrices: initialShowPrices,
  itemizePrintItems: initialItemizePrintItems,
  canSetDefaultScenario,
  onClose,
  onSave,
}) => {
  const { t } = useI18n();
  const resolvedTitle = title ?? t('scenario.editTitle');
  const resolvedSaveLabel = saveLabel ?? t('scenario.saveButton');
  const [nextName, setNextName] = useState(scenarioName);
  const [gridCols, setGridCols] = useState(gridSize.cols);
  const [gridRows, setGridRows] = useState(gridSize.rows);
  const [isDefault, setIsDefault] = useState(initialIsDefault);
  const [showPrices, setShowPrices] = useState(initialShowPrices);
  const [itemizePrintItems, setItemizePrintItems] = useState(initialItemizePrintItems);
  const clampGridValue = (value: number) => {
    if (Number.isNaN(value)) {
      return 4;
    }
    return Math.min(48, Math.max(4, value));
  };

  return (
    <ModalShell onClose={onClose} ariaLabel={resolvedTitle} className="modal scenario-settings-modal">
      <h3 style={{ marginTop: 0 }}>{resolvedTitle}</h3>

      <label>{t('scenario.name')}</label>
      <input
        type="text"
        value={nextName}
        onChange={(event) => setNextName(event.target.value)}
      />

      <div className="scenario-settings-grid">
        <div>
          <label>{t('scenario.columns')}</label>
          <NumberInput
            value={gridCols}
            onChange={(value) => setGridCols(clampGridValue(value))}
            min={4}
            max={48}
            className="number-input--scenario"
          />
        </div>
        <div>
          <label>{t('scenario.rows')}</label>
          <NumberInput
            value={gridRows}
            onChange={(value) => setGridRows(clampGridValue(value))}
            min={4}
            max={48}
            className="number-input--scenario"
          />
        </div>
      </div>

      {canSetDefaultScenario && (
        <label className="scenario-settings-checkbox">
          <input
            type="checkbox"
            checked={isDefault}
            onChange={(event) => setIsDefault(event.target.checked)}
          />
          {t('scenario.setDefault')}
        </label>
      )}

      <label className="scenario-settings-checkbox">
        <input
          type="checkbox"
          checked={showPrices}
          onChange={(event) => setShowPrices(event.target.checked)}
        />
        {t('scenario.showPrices')}
      </label>

      <label className="scenario-settings-checkbox">
        <input
          type="checkbox"
          checked={itemizePrintItems}
          onChange={(event) => setItemizePrintItems(event.target.checked)}
        />
        {t('scenario.itemizePrintItems')}
      </label>

      <p className="scenario-settings-note">
        {t('scenario.note')}
      </p>

      <div className="scenario-settings-actions">
        <button type="button" className="secondary" onClick={onClose}>
          {t('scenario.close')}
        </button>
        <button
          type="button"
          className="primary"
          onClick={() => onSave({
            currentName: scenarioName,
            nextName: nextName.trim(),
            gridCols,
            gridRows,
            isDefault,
            showPrices,
            itemizePrintItems,
          })}
          disabled={!nextName.trim()}
        >
          {resolvedSaveLabel}
        </button>
      </div>
    </ModalShell>
  );
};

export default ScenarioSettingsModal;
